import React, { useState, useEffect, useCallback } from 'react';
import { showAppAlert } from '../utils/alertService';
import AddAlertModal from './AddAlertModal';
import EditAlertModal from './EditAlertModal';
import LoaderOverlay from './LoaderOverlay';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function AlertList() {
  const [alerts, setAlerts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('token');
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [expandedMsgId, setExpandedMsgId] = useState(null);
  const [expandedRecipientsId, setExpandedRecipientsId] = useState(null);

  // Função para verificar se uma string é JSON válida
  const isValidJSON = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Busca os alertas da API
  const fetchAlerts = useCallback(async () => {
    try {
      const companyId = localStorage.getItem('company_id');
      const response = await fetch(`${API_URL}/alerts?company_id=${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const formattedAlerts = data.map((alert) => ({
          id: alert.id,
          nome: alert.alert_name,
          mensagem: alert.alert_msg,
          destinatarios: isValidJSON(alert.recipients)
            ? JSON.parse(alert.recipients).join(', ')
            : alert.recipients || 'Nenhum destinatário',
          status: alert.status === 1 ? 'Ativo' : 'Inativo',
          data_inicio: alert.start_date.split(' ')[0],
          data_termino: alert.end_date.split(' ')[0],
        }));

        const sortedAlerts = formattedAlerts.sort((a, b) => {
          if (a.status === 'Ativo' && b.status === 'Inativo') return -1;
          if (a.status === 'Inativo' && b.status === 'Ativo') return 1;
          return new Date(a.data_inicio) - new Date(b.data_inicio);
        });

        setAlerts(sortedAlerts);
      } else {
        const errorData = await response.json();
        setAlerts([]);
        showAppAlert('error', errorData.message || errorData.error || 'Erro ao buscar alertas.');
      }
    } catch (error) {
      setAlerts([]);
      console.error('Erro ao buscar alertas:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Função para ordenar os alertas
  const sortAlerts = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedAlerts = [...alerts].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setAlerts(sortedAlerts);
  };

  // Função para formatar data para o padrão DD-MM-AAAA
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Adiciona 'T00:00:00' para evitar problemas de fuso horário com datas no formato YYYY-MM-DD
    const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00`);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Mês é base 0
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Função para excluir um alerta
  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_URL}/alerts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        showAppAlert('success', 'Alerta excluído com sucesso!');
        fetchAlerts();
      } else {
        showAppAlert('error', 'Erro ao excluir o alerta.');
      }
    } catch (error) {
      console.error('Erro ao excluir o alerta:', error);
    }
  };

  // Função para abrir o modal de edição
  const handleEdit = (alert) => {
    setSelectedAlert(alert);
    setIsEditModalOpen(true);
  };

  // Função para fechar o modal de edição
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedAlert(null);
    fetchAlerts();
  };

  // Função para executar um alerta
  const handleExecute = async (id, status, startDate) => {
    // Validação para não permitir executar alertas inativos
    if (status === 'Inativo') {
      showAppAlert('warning', 'Não é possível executar um alerta inativo!');
      return;
    }

    setIsLoading(true);

    const qtdCompanies = localStorage.getItem('qtd_companies') || '1';
    const payload = {
      qtdCompanies: Number(qtdCompanies),
    };

    try {
      const response = await fetch(`${API_URL}/alerts/send/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const result = await response.json();
        showAppAlert('success', result.message || 'Alerta enviado com sucesso!');
      } else {
        const errorData = await response.json();
        showAppAlert('error', errorData.error || errorData.message || 'Erro ao executar o alerta.');
      }
    } catch (error) {
      showAppAlert('error', 'Erro ao executar o alerta.');
    } finally {
      setIsLoading(false);
    }
  };

  // Função para verificar se o alerta está vencido
  const isAlertExpired = (endDate) => {
    const today = new Date().toISOString().split('T')[0];
    return endDate < today;
  };

  // Função para obter o ícone de ordenação
  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '▲' : '▼';
    }
    return '';
  };

  // Componente para renderizar a célula de mensagem com "Ver mais/menos"
  const MessageCell = ({ alert }) => {
    const isExpanded = expandedMsgId === alert.id;
    const isLongText = alert.mensagem && alert.mensagem.length > 20;

    const toggleMessage = () => {
      setExpandedMsgId(isExpanded ? null : alert.id);
    };

    return (
      <td>
        {isLongText && !isExpanded ? `${alert.mensagem.substring(0, 20)}...` : alert.mensagem}
        {isLongText && (
          <button onClick={toggleMessage} className="link-button" style={{ marginLeft: '5px', background: 'none', border: 'none', color: 'blue', cursor: 'pointer', padding: 0 }}>
            {isExpanded ? 'ver menos' : 'ver mais'}
          </button>
        )}
      </td>
    );
  };

  // Componente para renderizar a célula de destinatários com "Ver mais/menos"
  const RecipientsCell = ({ alert }) => {
    const isExpanded = expandedRecipientsId === alert.id;
    const displayValue = alert.destinatarios === 'all' ? 'Todos Clientes Ativos' : alert.destinatarios;
    const isLongText = displayValue && displayValue.length > 20;

    const toggleRecipients = () => {
      setExpandedRecipientsId(isExpanded ? null : alert.id);
    };

    return (
      <td>
        {isLongText && !isExpanded ? `${displayValue.substring(0, 20)}...` : displayValue}
        {isLongText && (
          <button onClick={toggleRecipients} className="link-button" style={{ marginLeft: '5px', background: 'none', border: 'none', color: 'blue', cursor: 'pointer', padding: 0 }}>
            {isExpanded ? 'ver menos' : 'ver mais'}
          </button>
        )}
      </td>
    );
  };

  return (
    <div>
      <LoaderOverlay isLoading={isLoading} message="Enviando alerta..." />
      <table className="dashboard-table">
        <thead>
          <tr>
            <th className="hidden-column">ID</th>
            <th onClick={() => sortAlerts('nome')}>
              Nome {getSortIcon('nome')}
            </th>
            <th onClick={() => sortAlerts('mensagem')}>
              Mensagem {getSortIcon('mensagem')}
            </th>
            <th onClick={() => sortAlerts('destinatarios')}>
              Lista de Destinatários {getSortIcon('destinatarios')}
            </th>
            <th onClick={() => sortAlerts('data_inicio')}>
              Data Início {getSortIcon('data_inicio')}
            </th>
            <th onClick={() => sortAlerts('data_termino')}>
              Data Término {getSortIcon('data_termino')}
            </th>
            <th onClick={() => sortAlerts('status')}>
              Status {getSortIcon('status')}
            </th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {alerts.length > 0 ? (
            alerts.map((alert, index) => {
              const isExpired = isAlertExpired(alert.data_termino);
              return (
                <tr
                  key={alert.id}
                  className={index % 2 === 0 ? 'even-row' : 'odd-row'}
                >
                  <td className="hidden-column">{alert.id}</td>
                  <td>{alert.nome}</td>
                  <MessageCell alert={alert} />
                  <RecipientsCell alert={alert} />
                  <td>{formatDate(alert.data_inicio)}</td>
                  <td>{formatDate(alert.data_termino)}</td>
                  <td>{alert.status}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        className={`action-button ${alert.status === 'Inativo' || isExpired ? 'disabled-button' : ''}`}
                        title={
                          alert.status === 'Inativo'
                            ? 'Não é possível executar alertas inativos'
                            : isExpired
                            ? 'Não é possível executar alertas vencidos'
                            : 'Executar'
                        }
                        onClick={() => handleExecute(alert.id, alert.status)}
                        disabled={isLoading || isExpired || alert.status === 'Inativo'}
                      >
                        ▶
                      </button>
                      <button
                        onClick={() => handleEdit(alert)}
                        title="Editar"
                        className="action-button"
                        disabled={isLoading}
                      >
                        ✎
                      </button>
                      <button
                        className="action-button"
                        title="Excluir"
                        onClick={() => handleDelete(alert.id)}
                        disabled={isLoading}
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="9" style={{ textAlign: 'center' }}>
                Nenhum alerta encontrado.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div style={{ marginTop: '10px', textAlign: 'right' }}>
        <button
          title="Adicionar Alerta"
          className="button-style"
          onClick={() => setIsModalOpen(true)}
          disabled={isLoading}
        >
          +
        </button>
      </div>
      {/* Mensagens removidas - usar apenas modais temáticos */}
      <AddAlertModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddAlert={(newAlert) => {
          setAlerts(prev => [newAlert, ...prev]);
          fetchAlerts();
        }}
      />
      <EditAlertModal
        selectedAlert={selectedAlert}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onUpdate={fetchAlerts}
      />
    </div>
  );
}

export default AlertList;
